import React, {useContext, useRef, useState} from "react";
import PagedSearchTable, {PagedTableFunctions} from "../components/PagedSearchTable";
import ApiInterceptorController from "../controllers/ApiInterceptorController";
import ApiInterceptorRow from "../controllers/ApiInterceptorRow";
import ApiInterceptor from "../controllers/ApiInterceptor";
import Dialog from "../components/Dialog";
import AppContext from "../appContext";
import {wrapLoader} from "../wrapper";
import Success from "../components/Success";
import {ClipboardDocumentIcon} from "@heroicons/react/24/outline";
import {dateTimeFormat} from "../date";

const ManageApiInterceptors: React.FC = () => {
    const pagedTableRef = useRef<PagedTableFunctions<ApiInterceptorRow>>()
    const context = useContext(AppContext)
    const [detail, setDetail] = useState<ApiInterceptor | null>(null)

    function viewDetail(id: number) {
        wrapLoader(context, ApiInterceptorController.detail(id), resp => {
            setDetail(resp)
        })
    }

    function formatDate(dateStr: string | Date): string {
        if (!dateStr) return '';
        return dateTimeFormat(new Date(dateStr));
    }

    function copyToClipboard(text: string) {
        navigator.clipboard.writeText(text).then(() => {
            context.showSnack(<Success title="Copied to clipboard"/>)
        })
    }

    function duration(row: ApiInterceptorRow): string {
        const req = new Date(row.requestDate).getTime();
        const resp = new Date(row.responseDate).getTime();
        const ms = resp - req;
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    }

    return <div>
        <PagedSearchTable componentRef={pagedTableRef}
                          call={ApiInterceptorController.paged}
                          columns={[
                              {
                                  header: 'Id',
                                  key: 'id',
                                  row: (row) => row.id
                              },
                              {
                                  header: 'Source',
                                  key: 'source',
                                  row: (row) => <span>{row.sourceType} ({row.sourceId})</span>
                              },
                              {
                                  header: 'URL',
                                  key: 'url',
                                  row: (row) => <span className="text-xs break-all">{row.url}</span>
                              },
                              {
                                  header: 'Request Date',
                                  key: 'requestDate',
                                  row: (row) => formatDate(row.requestDate)
                              },
                              {
                                  header: 'Duration',
                                  key: 'duration',
                                  row: (row) => duration(row)
                              },
                              {
                                  header: <div className='text-right'>Actions</div>,
                                  key: 'actions',
                                  row: (row) => <div className='text-right'>
                                      <div className="bg-primary btn-sm"
                                           onClick={() => viewDetail(row.id)}>
                                          view
                                      </div>
                                  </div>
                              }
                          ]}/>

        <Dialog title={"API Interceptor Detail"}
                show={detail != null}
                setShow={() => setDetail(null)}
                maxHeight="80vh"
                body={detail ? <div className="p-4 space-y-4 min-w-[600px]">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><strong>Id:</strong> {detail.id}</div>
                        <div><strong>Source:</strong> {detail.sourceType} ({detail.sourceId})</div>
                        <div><strong>URL:</strong> {detail.url}</div>
                        <div><strong>Duration:</strong> {(() => {
                            const ms = new Date(detail.responseDate).getTime() - new Date(detail.requestDate).getTime();
                            return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
                        })()}</div>
                        <div><strong>Request Date:</strong> {formatDate(detail.requestDate)}</div>
                        <div><strong>Response Date:</strong> {formatDate(detail.responseDate)}</div>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <strong className="text-sm">Request:</strong>
                            <ClipboardDocumentIcon className="h-4 w-4 text-gray-500 hover:text-primary-500 cursor-pointer" onClick={() => copyToClipboard(detail.request)}/>
                        </div>
                        <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-[30vh] whitespace-pre-wrap break-all">{detail.request}</pre>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <strong className="text-sm">Response:</strong>
                            <ClipboardDocumentIcon className="h-4 w-4 text-gray-500 hover:text-primary-500 cursor-pointer" onClick={() => copyToClipboard(detail.response)}/>
                        </div>
                        <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-[30vh] whitespace-pre-wrap break-all">{detail.response}</pre>
                    </div>
                </div> : null}/>
    </div>
}

export default ManageApiInterceptors;
